<script lang="ts">
  import Breadcrumb from "$lib/Breadcrumb.svelte";
  import MetaData from "$lib/MetaData.svelte";
  import { contentBlockComponents } from "$lib/portableText/utils";
  import type { AllContractsPage, PodioContract } from "$lib/types/allContracts";
  import { localeRound } from "$lib/utils/math";
  import { PortableText } from "@portabletext/svelte";
  import { onMount } from "svelte";
  interface Props {
    data: {
      allContracts: PodioContract[];
      page: AllContractsPage;
    };
  }
  let { data }: Props = $props();
  let noCustomScrollbar = $state(false);
  onMount(() => {
    noCustomScrollbar = navigator.userAgent?.includes("iPhone");
  });
  let allContracts = $derived(data.allContracts);
  let page = $derived(data?.page);
  let activeDeals = $derived(
    allContracts instanceof Array &&
      allContracts
        .filter((c) => c.Aktiv === "Ja")
        .sort((a, b) => Number(a.Sortering) - Number(b.Sortering))
  );
  let inactiveDeals = $derived(
    allContracts instanceof Array && allContracts.filter((c) => c.Aktiv === "Nei")
  );
  const isNumericRange = (value: string | null): boolean => {
    if (!value) return false;
    return (
      value.split("-").length === 2 &&
      !isNaN(Number(value.split("-")[0])) &&
      !isNaN(Number(value.split("-")[1]))
    );
  };
  const formatNumber = (value: string, decimals: number = 4): string => {
    if (Number(value) === 0) return "0";
    return localeRound(Number(value), { decimals, minDecimals: decimals });
  };
  const formatRange = (value: string): string => {
    let [first, second] = value.split("-");
    return `${formatNumber(first)} - ${formatNumber(second)}`;
  };
</script>

<MetaData
  data={{
    title: "Prisliste for privatkunder",
    description: "Prisliste over alle avtaler som tilbys av Ishavskraft",
  }}
/>
<Breadcrumb
  breadcrumbs={[
    { label: "Forside", href: "/" },
    { label: "Prisliste - Alle Avtaler", href: "/alle-avtaler" },
  ]}
/>
{#snippet table({ contracts, title }: { contracts: PodioContract[]; title: string })}
  <h2>{title}</h2>
  <div class="table_wrapper">
    <table>
      <tbody>
        <tr>
          <th scope="row" class="text-left">Navn</th>
          <th scope="row" class="text-left">Avtaletype</th>
          <th scope="row" class="text-left">Påslag</th>
          <th scope="row" class="text-left">Pris eller prisspenn</th>
          <th scope="row" class="text-left">Fastbeløp</th>
          <th scope="row" class="text-left">Papirfaktura</th>
          <th scope="row" class="text-left">Tilgjengelig for</th>
          <th scope="row" class="text-left">Informasjon</th>
          <th scope="row" class="text-left">Avtalevilkår</th>
        </tr>
        {#each contracts as contract (contract.Navn)}
          <tr>
            <td class="max-w-[65ch] overflow-y-auto overflow-auto">{contract.Navn}</td>
            <td class="max-w-[65ch] overflow-y-auto overflow-auto">{contract.Avtaletype}</td>
            <td class="max-w-[65ch] overflow-y-auto overflow-auto">
              {#if isNumericRange(contract.Paaslag)}
                {formatRange(contract.Paaslag)} øre/kWh
              {:else if !isNaN(Number(contract.Paaslag))}
                {formatNumber(contract.Paaslag)} øre/kWh
              {:else}
                {contract.Paaslag}
              {/if}
            </td>
            <td class="max-w-[65ch] overflow-y-auto overflow-auto">
              {#if contract.Pris_eller_spenn}
                {#if isNumericRange(contract.Pris_eller_spenn)}
                  {formatRange(contract.Pris_eller_spenn)} øre/kWh
                {:else if !isNaN(Number(contract.Pris_eller_spenn))}
                  {formatNumber(contract.Pris_eller_spenn)} øre/kWh
                {:else}
                  {contract.Pris_eller_spenn}
                {/if}
              {:else}
                -
              {/if}
            </td>
            <td class="max-w-[65ch] overflow-y-auto overflow-auto">
              {formatNumber(contract.Fastbelop, 2)} kr
            </td>
            <td class="max-w-[65ch] overflow-y-auto overflow-auto">
              {formatNumber(contract.Fakturagebyr, 2)} kr
            </td>
            <td class="max-w-[65ch] overflow-y-auto overflow-auto">{contract.Tilgjengeligfor}</td>
            <td class="max-w-[65ch] overflow-y-auto overflow-auto">{contract.Informasjon}</td>
            <td class="max-w-[65ch] overflow-y-auto overflow-auto">
              <a href={contract.TermsUrl}>Avtalevilkår</a>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/snippet}

<section class="mx-edge-x">
  <h1 class="my-10">{page?.title || "Prisliste for privatkunder"}</h1>

  <div class="tables_content mt-8">
    {#if noCustomScrollbar}
      <p class="text-sm">Scroll sidelengs i tabellen for å se mer →</p>
    {/if}
    {#if page?.content}
      <PortableText
        value={page.content}
        components={contentBlockComponents}
        onMissingComponent={false}
      />
    {/if}
    {#if activeDeals && inactiveDeals}
      {@render table({ contracts: activeDeals, title: "Aktive avtaler" })}
      {@render table({ contracts: inactiveDeals, title: "Avtaler som ikke er i salg" })}
    {/if}
  </div>
</section>

<style>
  .tables_content {
    font-size: var(--text-sm);
  }

  .table_wrapper {
    border: 1px solid;
    margin-block-end: var(--s-8);
  }
  table {
    border: none;
    margin-block: 0;
    overflow-x: scroll;
    -ms-overflow-style: scrollbar; /* IE and Edge */
    scrollbar-color: var(--aurora, var(--secondary-pink-800)) var(--primary-white); /* Firefox */
    scrollbar-width: auto;
  }

  ::-webkit-scrollbar {
    -webkit-appearance: none;
    height: var(--s-3);
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 0.5rem;
    background-color: var(--aurora, var(--secondary-pink-800));
    box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
  }

  ::-webkit-scrollbar-track {
    background-color: var(--primary-white);
  }

  tr {
    border: none;
  }

  th,
  td {
    white-space: pre;
    width: 10%;
    padding-inline: var(--s-4);
  }

  tr:nth-child(2n) {
    background-color: var(--primary-white);
  }
</style>
